import { useState } from "react";
import { Loader2, CreditCard, AlertTriangle, RefreshCw, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { autoRechargeSetup, autoRechargeSave } from "@/lib/api";
import { toast } from "sonner";

const eur = (n) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(
    Number(n) || 0
  );

export default function AutoRecharge({ data, onSaved }) {
  const ar = data.auto_recharge || {};
  const hasCard = !!data.has_saved_card;
  const [enabled, setEnabled] = useState(!!ar.enabled);
  const [threshold, setThreshold] = useState(ar.threshold ?? 20);
  const [pkg, setPkg] = useState(ar.package || "p100");
  const [saving, setSaving] = useState(false);
  const [cardBusy, setCardBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const saveCard = async () => {
    setCardBusy(true);
    try {
      const { url } = await autoRechargeSetup(window.location.origin);
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Karte konnte nicht hinterlegt werden.");
      setCardBusy(false);
    }
  };

  const save = async (nextEnabled) => {
    const en = typeof nextEnabled === "boolean" ? nextEnabled : enabled;
    if (en && !hasCard) {
      toast.error("Bitte zuerst eine Karte hinterlegen.");
      return;
    }
    setSaving(true);
    try {
      await autoRechargeSave({ enabled: en, threshold: Number(threshold) || 0, package: pkg });
      setEnabled(en);
      toast.success("Auto-Aufladung gespeichert.");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-border bg-white" data-testid="auto-recharge-card">
      <div className="flex items-center gap-2 px-4 py-3">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-2 text-left flex-1 min-w-0 hover:opacity-80 transition-opacity"
          data-testid="auto-recharge-card-toggle"
        >
          <RefreshCw className="h-5 w-5 text-primary shrink-0" />
          <div className="min-w-0">
            <h2 className="font-medium">Automatische Aufladung</h2>
          </div>
          {open ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground ml-auto shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground ml-auto shrink-0" />
          )}
        </button>
        {open && (
          <Switch
            checked={enabled}
            onCheckedChange={(v) => save(v)}
            disabled={saving || !hasCard}
            data-testid="auto-recharge-toggle"
          />
        )}
      </div>

      {open && (
        <div className="border-t border-border p-4 sm:p-6">
          {ar.failed && (
            <div
              className="flex items-start gap-2 text-sm rounded-md bg-destructive/10 border border-destructive p-3 text-destructive"
              data-testid="auto-recharge-failed"
            >
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <p>
                Die letzte automatische Aufladung ist fehlgeschlagen und wurde pausiert. Bitte
                hinterlegen Sie eine gültige Karte erneut.
              </p>
            </div>
          )}

          <div className={`${ar.failed ? "mt-4 " : ""}space-y-4`}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-md border border-border p-3">
              <div className="flex items-center gap-2 text-sm">
                <CreditCard className="h-4 w-4 text-muted-foreground" />
                {hasCard ? (
                  <span data-testid="saved-card">
                    {(data.card_brand || "Karte").toUpperCase()} •••• {data.card_last4 || "????"}
                  </span>
                ) : (
                  <span className="text-muted-foreground" data-testid="no-saved-card">
                    Keine Karte hinterlegt
                  </span>
                )}
              </div>
              <Button
                variant="secondary"
                size="sm"
                className="border border-border gap-2"
                onClick={saveCard}
                disabled={cardBusy}
                data-testid="save-card-button"
              >
                {cardBusy && <Loader2 className="h-4 w-4 animate-spin" />}
                {hasCard ? "Karte ändern" : "Karte hinterlegen"}
              </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="data-label text-muted-foreground" htmlFor="ar-threshold">
                  Schwellwert (€)
                </label>
                <Input
                  id="ar-threshold"
                  type="number"
                  min="0"
                  step="5"
                  value={threshold}
                  onChange={(e) => setThreshold(e.target.value)}
                  className="bg-white mt-1"
                  data-testid="auto-recharge-threshold"
                />
              </div>
              <div>
                <span className="data-label text-muted-foreground">Aufladebetrag</span>
                <Select value={pkg} onValueChange={setPkg}>
                  <SelectTrigger className="bg-white mt-1" data-testid="auto-recharge-package">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(data.packages || []).map((p) => (
                      <SelectItem key={p.id} value={p.id} data-testid={`ar-pkg-${p.id}`}>
                        {eur(p.net)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button onClick={() => save()} disabled={saving} data-testid="auto-recharge-save">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Einstellungen speichern
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
